import { useEffect, useRef, type ReactNode } from 'react';
import { audio } from '../game/audio';
import type { MeResponse } from '../game/api';
import { getSettings } from '../game/settings';
import { t, type Key } from '../i18n';
import { Amount, Icon, type IconName } from './icons';
import { MenuArena } from './menuArena';

/** Pages légales : mentions (#legal), confidentialité (#privacy), conditions de vente (#terms). */
export type LegalPageId = 'legal' | 'privacy' | 'terms';
export type Page = 'hangar' | 'tree' | 'pass' | 'shop' | 'paint' | 'forge' | 'friends' | 'profile' | 'settings' | 'help' | 'admin' | LegalPageId;
export const PAGES: readonly Page[] = ['hangar', 'tree', 'pass', 'shop', 'paint', 'forge', 'friends', 'profile', 'settings', 'help', 'admin', 'legal', 'privacy', 'terms'];

const NAV: { page: Page; icon: IconName }[] = [
  { page: 'hangar', icon: 'hangar' },
  { page: 'tree', icon: 'tree' },
  { page: 'pass', icon: 'pass' },
  { page: 'shop', icon: 'shop' },
  { page: 'friends', icon: 'friends' },
];

/** Page indiquée par l'adresse (#pass, #shop…) : le bouton Retour du téléphone revient à la page précédente. */
export function pageFromHash(): Page {
  const h = location.hash.slice(1);
  return (PAGES as readonly string[]).includes(h) ? (h as Page) : 'hangar';
}

/** Arène animée derrière les menus ; figée avec « animations réduites », en pause quand l'onglet est caché. */
function MenuBackground() {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const s = getSettings();
    let arena: MenuArena | null = null;
    let cancelled = false;
    void MenuArena.create(el, { still: s.reducedMotion, quality: s.quality })
      .then((a) => {
        if (cancelled) a.destroy();
        else arena = a;
      })
      .catch(() => {});
    const vis = () => arena?.setPaused(document.hidden);
    const resize = () => arena?.resize();
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('resize', resize);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', vis);
      window.removeEventListener('resize', resize);
      arena?.destroy();
    };
  }, []);
  return <div className="menu-bg" ref={host} aria-hidden />;
}

/** Sons d'interface délégués : survol des commandes principales, clic de tout bouton actif. */
function useUiSounds() {
  const last = useRef<Element | null>(null);
  return {
    onPointerOver: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const el = (e.target as HTMLElement).closest('.nav-item, .mode-row, .tile, .pass-tile, .btn-primary');
      if (el && el !== last.current && !(el as HTMLButtonElement).disabled) audio.play('uiHover');
      last.current = el;
    },
    onClick: (e: React.MouseEvent) => {
      const el = (e.target as HTMLElement).closest('button:not(:disabled), [role="radio"]');
      if (el) audio.play(el.closest('.menu-nav') ? 'uiNav' : 'uiClick');
    },
  };
}

export function MenuShell({
  page,
  go,
  me,
  badges,
  children,
}: {
  page: Page;
  go: (p: Page) => void;
  me: MeResponse | null;
  badges?: Partial<Record<Page, number>>;
  children: ReactNode;
}) {
  const sounds = useUiSounds();
  const account = me?.account;
  return (
    <div className="menu-root" data-page={page} {...sounds}>
      <MenuBackground />
      <header className="topbar">
        <button type="button" className="brand" onClick={() => go('hangar')} aria-label={`TANKSHIFT — ${t('nav_hangar')}`}>
          <span className="logo display" aria-hidden>
            TANK<span>SHIFT</span>
          </span>
          <span className="brand-stripe" aria-hidden />
        </button>
        <nav className="menu-nav" aria-label={t('navMain')}>
          {NAV.map((n) => (
            <button key={n.page} type="button" className={`nav-item ${page === n.page ? 'on' : ''}`} aria-current={page === n.page ? 'page' : undefined} onClick={() => go(n.page)}>
              <Icon name={n.icon} size={20} />
              <span className="nav-label">{t(`nav_${n.page}` as Key)}</span>
              {(badges?.[n.page] ?? 0) > 0 && <span className="nav-badge num">{badges![n.page]}</span>}
            </button>
          ))}
        </nav>
        <div className="topbar-end">
          {me && (
            <button type="button" className="wallet" onClick={() => go('shop')} aria-label={t('walletLabel', { c: me.balances.coins ?? 0, g: me.balances.gems ?? 0 })}>
              <Amount kind="coins" n={me.balances.coins ?? 0} />
              <Amount kind="gems" n={me.balances.gems ?? 0} />
            </button>
          )}
          <button type="button" className={`account-chip ${page === 'profile' ? 'on' : ''}`} onClick={() => go('profile')} data-testid="account">
            <Icon name="profile" size={18} />
            <span className="account-name">{account ? account.name : t('guestChip')}</span>
            {account && !account.verified && <span className="account-dot" aria-label={t('notVerified')} />}
          </button>
          <button type="button" className={`icon-btn ${page === 'help' ? 'on' : ''}`} aria-label={t('help')} onClick={() => go('help')}>
            <Icon name="help" />
          </button>
          <button type="button" className={`icon-btn ${page === 'settings' ? 'on' : ''}`} aria-label={t('settings')} onClick={() => go('settings')}>
            <Icon name="settings" />
          </button>
        </div>
      </header>
      <main className="menu-main" key={page}>
        {children}
      </main>
    </div>
  );
}

/** En-tête de page : titre pochoir, sous-titre et actions à droite. */
export function PageHead({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="page-head-text">
        <h1 className="page-title display">{title}</h1>
        {sub && <p className="page-sub">{sub}</p>}
      </div>
      {children && <div className="page-head-end">{children}</div>}
    </div>
  );
}

/** Feuille plein écran au-dessus de la partie (arbre, paramètres, aide) ; Échap ferme. */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const sounds = useUiSounds();
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);
  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title} {...sounds}>
      <header className="sheet-head">
        <h2>{title}</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('close')}>
          <Icon name="close" />
        </button>
      </header>
      <div className="sheet-body">{children}</div>
    </div>
  );
}
